import { isNil } from 'lodash';
import { Preferences } from '@capacitor/preferences';
import { v4 as uuidv4 } from 'uuid';
import { Store } from 'pullstate';
import { PushNotificationService } from './services/pushNotificationService';

// Updates the notification service with the current list of unique tickers from all portfolios
const updateNotificationTickers = async (portfolios: Portfolio[]) => {
  try {
    // Extract all unique symbols from all portfolios
    const allSymbols = Array.from(new Set(portfolios.flatMap((p) => p.symbols || [])));

    // Check if notifications are enabled
    const savedPreferences = localStorage.getItem('notificationPreferences');
    if (!savedPreferences) {
      return;
    }

    // Get enabled notification types
    const preferences = JSON.parse(savedPreferences);
    const enabledTypes = preferences
      .filter((pref: { enabled: boolean }) => pref.enabled)
      .map((pref: { id: string }) => pref.id);

    // Only update if there are enabled notification types
    if (enabledTypes.length > 0) {
      // Set a timeout to prevent blocking indefinitely
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('PushNotificationService updateConfig timeout')), 5000),
      );

      // Update notification service with current portfolio symbols
      await Promise.race([
        PushNotificationService.updateConfig({
          notificationTypes: enabledTypes,
          watchlistSymbols: allSymbols, // Using the variable name watchlistSymbols for API compatibility
        }),
        timeoutPromise,
      ]);
    }
  } catch (error) {
    console.warn('Error updating notification tickers:', error);
    // Continue app flow despite notification errors
  }
};

// Small delay to let IndexedDB transactions settle
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Low-level storage operations with retry logic for iOS WKWebView IndexedDB errors
// See: https://getbloom.sentry.io/issues/4624579958/

const setRaw = async (key: string, value: string): Promise<void> => {
  try {
    await Preferences.set({ key, value });
  } catch (error) {
    console.warn(`[Storage] Set failed for key "${key}", retrying...`, error);
    await delay(50);
    try {
      await Preferences.set({ key, value });
    } catch (retryError) {
      console.warn(`[Storage] Set retry failed for key "${key}":`, retryError);
    }
  }
};

const getRaw = async (key: string): Promise<string | null> => {
  try {
    const ret = await Preferences.get({ key });
    return ret.value;
  } catch (error) {
    console.warn(`[Storage] Get failed for key "${key}", retrying...`, error);
    await delay(50);
    try {
      const ret = await Preferences.get({ key });
      return ret.value;
    } catch (retryError) {
      console.warn(`[Storage] Get retry failed for key "${key}":`, retryError);
      return null;
    }
  }
};

const remove = async (key: string): Promise<void> => {
  try {
    await Preferences.remove({ key });
  } catch (error) {
    console.warn(`[Storage] Remove failed for key "${key}", retrying...`, error);
    await delay(50);
    try {
      await Preferences.remove({ key });
    } catch (retryError) {
      console.warn(`[Storage] Remove retry failed for key "${key}":`, retryError);
    }
  }
};

// JSON wrapper functions for complex objects
export const set = async (key: string, value: unknown): Promise<void> => {
  await setRaw(key, JSON.stringify(value));
};

export const get = async <Type>(key: string): Promise<Type | undefined> => {
  const value = await getRaw(key);
  if (isNil(value)) return undefined;
  return JSON.parse(value);
};

// Interface for portfolio data
export interface Allocation {
  symbol: string;
  name: string;
  allocation_percentage?: number;
  rationale: string;
}

export interface PortfolioEvent {
  date: string;
  changes: string[];
}

export interface Portfolio {
  id: string;
  title: string;
  strategy: string; // Optional, keep for compatibility
  symbols: string[]; // Add this field for stock symbols
  allocations?: Allocation[]; // Make allocations optional for watchlist-only portfolios
  total_allocation?: number;
  events?: PortfolioEvent[]; // Add events to track portfolio updates
}

// Type for minimal portfolio data needed for selection modals
export interface PortfolioMinimal {
  id: string;
  title: string;
  symbols: string[];
}

// Portfolio Store
export const portfoliosStoreId = 'portfolios';

interface PortfoliosState {
  portfolios: Portfolio[];
  isLoading: boolean;
}

export const Portfolios = new Store<PortfoliosState>({
  portfolios: [],
  isLoading: false,
});

// Initialize the portfolio store and setup persistence
export const initializePortfolios = async () => {
  // Setup persistence by subscribing to store changes
  Portfolios.subscribe(
    (s) => s.portfolios,
    (portfolios) => {
      set(portfoliosStoreId, portfolios);
    },
  );

  // Load initial data
  try {
    Portfolios.update((s) => {
      s.isLoading = true;
    });
    const savedPortfolios = await get<Portfolio[]>(portfoliosStoreId);

    Portfolios.update((s) => {
      s.portfolios = savedPortfolios || [];
      s.isLoading = false;
    });
  } catch (error) {
    console.error('Error loading portfolios:', error);
    Portfolios.update((s) => {
      s.isLoading = false;
    });
  }
};

// Get all portfolios from the store
export const getPortfolios = async (): Promise<Portfolio[]> => {
  // If the store hasn't been initialized yet, fetch from storage directly
  if (Portfolios.getRawState().portfolios.length === 0 && !Portfolios.getRawState().isLoading) {
    await initializePortfolios();
  }
  return Portfolios.getRawState().portfolios;
};

// Save a new portfolio to the store
export const savePortfolio = async (portfolio: Omit<Portfolio, 'id'>): Promise<string> => {
  const id = uuidv4();

  const newPortfolio = {
    id,
    ...portfolio,
    symbols: portfolio.symbols || [], // Default to empty array
    allocations: portfolio.allocations || [], // Default to empty array
  };

  Portfolios.update((s) => {
    s.portfolios = [...s.portfolios, newPortfolio];
  });

  // Refresh notification tickers after portfolio change
  await updateNotificationTickers(Portfolios.getRawState().portfolios);

  return id;
};

// Update an existing portfolio in the store
export const updatePortfolio = async (portfolio: Portfolio): Promise<boolean> => {
  try {
    const currentPortfolios = Portfolios.getRawState().portfolios;
    const index = currentPortfolios.findIndex((p) => p.id === portfolio.id);

    // If portfolio doesn't exist, return false
    if (index === -1) return false;

    Portfolios.update((s) => {
      s.portfolios = [
        ...s.portfolios.slice(0, index),
        {
          ...portfolio,
          symbols: portfolio.symbols || [], // Ensure symbols is an array
          allocations: portfolio.allocations || [], // Ensure allocations is an array
        },
        ...s.portfolios.slice(index + 1),
      ];
    });

    // Refresh notification tickers after portfolio change
    await updateNotificationTickers(Portfolios.getRawState().portfolios);

    return true;
  } catch (error) {
    console.error('Error updating portfolio:', error);
    return false;
  }
};

// Delete a portfolio from the store
export const deletePortfolio = async (id: string): Promise<{ success: boolean; deletedPortfolio?: Portfolio }> => {
  try {
    const currentPortfolios = Portfolios.getRawState().portfolios;
    const deletedPortfolio = currentPortfolios.find((portfolio) => portfolio.id === id);

    if (!deletedPortfolio) {
      return { success: false };
    }

    Portfolios.update((s) => {
      s.portfolios = s.portfolios.filter((portfolio) => portfolio.id !== id);
    });

    // Refresh notification tickers after portfolio change
    await updateNotificationTickers(Portfolios.getRawState().portfolios);

    return { success: true, deletedPortfolio };
  } catch (error) {
    console.error('Error deleting portfolio:', error);
    return { success: false };
  }
};

// Restore a deleted portfolio
export const restorePortfolio = async (portfolio: Portfolio): Promise<boolean> => {
  try {
    const currentPortfolios = Portfolios.getRawState().portfolios;

    // Check if a portfolio with this ID already exists
    if (currentPortfolios.some((p) => p.id === portfolio.id)) {
      return false;
    }

    Portfolios.update((s) => {
      s.portfolios = [...s.portfolios, portfolio];
    });

    // Refresh notification tickers after portfolio change
    await updateNotificationTickers(Portfolios.getRawState().portfolios);

    return true;
  } catch (error) {
    console.error('Error restoring portfolio:', error);
    return false;
  }
};

// Get only minimal portfolio data (id, title, symbols) from the store
export const getPortfolioMinimalData = async (): Promise<PortfolioMinimal[]> => {
  const portfolios = await getPortfolios();

  return portfolios.map((portfolio) => ({
    id: portfolio.id,
    title: portfolio.title,
    symbols: portfolio.symbols || [],
  }));
};

// Copy a collection to a new portfolio
export const copyCollectionToPortfolio = async (
  collectionName: string,
  strategy: string,
  allocations: Allocation[],
): Promise<string> => {
  if (!allocations || allocations.length === 0) {
    throw new Error('Cannot create portfolio: allocations array is empty');
  }

  const symbols = allocations.map((a) => a.symbol);

  const portfolioData = {
    title: collectionName,
    strategy,
    symbols,
    allocations,
  };

  return await savePortfolio(portfolioData);
};

// ============================================================================
// Plaid Integration Storage
// ============================================================================

// Note: PLAID_ACCESS_TOKEN_KEY was removed for security - tokens are stored server-side only
const PLAID_INSTITUTION_NAME_KEY = 'plaid_institution_name';
const PLAID_ITEM_ID_KEY = 'plaid_item_id';
const PLAID_PORTFOLIO_ID_KEY = 'plaid_portfolio_id';
const PLAID_PORTFOLIO_IDS_KEY = 'plaid_portfolio_ids';
const PLAID_PORTFOLIO_ACCOUNT_MAP_KEY = 'plaid_portfolio_account_map';
const PLAID_DEVICE_UUID_KEY = 'plaid_device_uuid';

/**
 * Get or create a persistent device UUID.
 * This UUID is permanent and survives Plaid disconnections.
 * It's only lost if the user deletes the app.
 */
export const getOrCreateDeviceUuid = async (): Promise<string> => {
  const existing = await Preferences.get({ key: PLAID_DEVICE_UUID_KEY });
  if (existing.value) {
    return existing.value;
  }
  const newUuid = uuidv4();
  await Preferences.set({ key: PLAID_DEVICE_UUID_KEY, value: newUuid });
  return newUuid;
};

export interface PlaidCredentials {
  institutionName: string | null;
  itemId: string | null;
  portfolioId: string | null;
  portfolioIds: string[] | null;
  deviceUuid: string | null;
}

/**
 * Save Plaid credentials to device storage.
 * Uses retry-enabled wrappers for iOS WKWebView compatibility.
 * Note: Access tokens are NOT stored client-side - they're encrypted server-side only.
 * @param itemId - The Plaid item ID (Plaid's identifier)
 * @param institutionName - The name of the connected institution
 * @param deviceUuid - Our device UUID used for backend connection lookup
 */
export const savePlaidCredentials = async (
  itemId: string,
  institutionName: string,
  deviceUuid: string,
): Promise<void> => {
  await setRaw(PLAID_ITEM_ID_KEY, itemId);
  await setRaw(PLAID_INSTITUTION_NAME_KEY, institutionName);
  await setRaw(PLAID_DEVICE_UUID_KEY, deviceUuid);
};

/**
 * Get Plaid credentials from device storage.
 * Uses retry-enabled wrappers for iOS WKWebView compatibility.
 */
export const getPlaidCredentials = async (): Promise<PlaidCredentials> => {
  const institutionName = await getRaw(PLAID_INSTITUTION_NAME_KEY);
  const itemId = await getRaw(PLAID_ITEM_ID_KEY);
  const portfolioId = await getRaw(PLAID_PORTFOLIO_ID_KEY);
  const portfolioIdsRaw = await getRaw(PLAID_PORTFOLIO_IDS_KEY);
  const deviceUuid = await getRaw(PLAID_DEVICE_UUID_KEY);

  let portfolioIds: string[] | null = null;
  if (portfolioIdsRaw) {
    try {
      portfolioIds = JSON.parse(portfolioIdsRaw);
    } catch {
      portfolioIds = null;
    }
  }

  return { institutionName, itemId, portfolioId, portfolioIds, deviceUuid };
};

/**
 * Clear Plaid credentials from device storage.
 * Uses retry-enabled wrappers for iOS WKWebView compatibility.
 * NOTE: deviceUuid is preserved to maintain backend snapshot history.
 * The deviceUuid is permanent and only lost if the user deletes the app.
 */
export const clearPlaidCredentials = async (): Promise<void> => {
  await remove(PLAID_ITEM_ID_KEY);
  await remove(PLAID_INSTITUTION_NAME_KEY);
  await remove(PLAID_PORTFOLIO_ID_KEY);
  await remove(PLAID_PORTFOLIO_IDS_KEY);
  await remove(PLAID_PORTFOLIO_ACCOUNT_MAP_KEY);
  // NOTE: We intentionally do NOT remove PLAID_DEVICE_UUID_KEY
  // This preserves the user's snapshot history in the backend
};

/**
 * Check if Plaid is connected (has valid item ID).
 * Note: We use itemId instead of accessToken since tokens are stored server-side only.
 */
export const isPlaidConnected = async (): Promise<boolean> => {
  const { itemId } = await getPlaidCredentials();
  return !!itemId;
};

/**
 * Get the Plaid portfolio ID if one exists (legacy single portfolio).
 */
export const getPlaidPortfolioId = async (): Promise<string | null> => {
  const credentials = await getPlaidCredentials();
  return credentials.portfolioId;
};

/**
 * Get all Plaid portfolio IDs (per-account portfolios).
 * Falls back to legacy single portfolio ID if no per-account IDs exist.
 */
export const getPlaidPortfolioIds = async (): Promise<string[]> => {
  const credentials = await getPlaidCredentials();
  if (credentials.portfolioIds && credentials.portfolioIds.length > 0) {
    return credentials.portfolioIds;
  }
  // Fallback to legacy single portfolio
  if (credentials.portfolioId) {
    return [credentials.portfolioId];
  }
  return [];
};

/**
 * Check if a given portfolio ID is a Plaid portfolio.
 */
export const isPlaidPortfolioId = async (portfolioId: string): Promise<boolean> => {
  const ids = await getPlaidPortfolioIds();
  return ids.includes(portfolioId);
};

/**
 * Get the mapping of portfolioId -> Plaid accountId.
 * Used to filter Plaid holdings to the correct account for display.
 */
export const getPlaidPortfolioAccountMap = async (): Promise<Record<string, string>> => {
  const raw = await getRaw(PLAID_PORTFOLIO_ACCOUNT_MAP_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

/**
 * Disconnect Plaid and delete the associated portfolio.
 * This resets the flow so user can reconnect fresh.
 * Also notifies backend to mark connection as inactive.
 */
export const disconnectPlaidAndDeletePortfolio = async (): Promise<void> => {
  const credentials = await getPlaidCredentials();

  // Notify backend to mark connection as inactive
  // This preserves historical snapshots for potential reconnection
  if (credentials.deviceUuid) {
    try {
      const { disconnectPlaid } = await import('./apis/plaid');
      await disconnectPlaid(credentials.deviceUuid);
    } catch {
      // Continue with local cleanup even if backend fails
    }
  }

  // Delete all per-account Plaid portfolios
  if (credentials.portfolioIds && credentials.portfolioIds.length > 0) {
    for (const id of credentials.portfolioIds) {
      await deletePortfolio(id);
    }
  }
  // Also delete legacy single portfolio if it exists
  if (credentials.portfolioId) {
    await deletePortfolio(credentials.portfolioId);
  }

  await clearPlaidSnapshots();
  await clearPlaidHoldingsCache();

  // Clear all Plaid credentials
  await clearPlaidCredentials();
};

/**
 * Create or update a portfolio from Plaid holdings (legacy single portfolio).
 * Returns the portfolio ID.
 * @deprecated Use savePlaidPortfolios for per-account portfolios.
 */
export const savePlaidPortfolio = async (institutionName: string, symbols: string[]): Promise<string> => {
  const credentials = await getPlaidCredentials();
  const portfolios = await getPortfolios();

  // Check 1: Look for existing portfolio by saved ID
  if (credentials.portfolioId) {
    const existingById = portfolios.find((p) => p.id === credentials.portfolioId);
    if (existingById) {
      const updatedPortfolio = {
        ...existingById,
        title: institutionName,
        symbols,
      };
      await updatePortfolio(updatedPortfolio);
      // Auto-select the Plaid portfolio
      localStorage.setItem('selectedPortfolioId', credentials.portfolioId);
      return credentials.portfolioId;
    }
  }

  // Check 2: Look for existing portfolio by title (prevents duplicates from race conditions)
  const existingByTitle = portfolios.find((p) => p.title === institutionName);
  if (existingByTitle) {
    const updatedPortfolio = {
      ...existingByTitle,
      symbols,
    };
    await updatePortfolio(updatedPortfolio);
    // Save the ID to credentials so future updates use it
    await setRaw(PLAID_PORTFOLIO_ID_KEY, existingByTitle.id);
    // Auto-select the Plaid portfolio
    localStorage.setItem('selectedPortfolioId', existingByTitle.id);
    return existingByTitle.id;
  }

  // Create new portfolio only if no existing one found
  const portfolioId = await savePortfolio({
    title: institutionName,
    strategy: '',
    symbols,
    allocations: [],
  });

  // Save the portfolio ID to Plaid credentials
  await setRaw(PLAID_PORTFOLIO_ID_KEY, portfolioId);

  // Auto-select the Plaid portfolio
  localStorage.setItem('selectedPortfolioId', portfolioId);

  return portfolioId;
};

/**
 * Account data for creating per-account Plaid portfolios.
 */
export interface PlaidAccountForPortfolio {
  accountId: string;
  name: string;
  subtype: string | null;
  symbols: string[];
}

/**
 * Create or update per-account portfolios from Plaid holdings.
 * Each Plaid account gets its own portfolio (e.g., "M1 Finance - IRA", "M1 Finance - Individual").
 * Handles migration from legacy single merged portfolio.
 * Returns array of portfolio IDs.
 */
export const savePlaidPortfolios = async (
  institutionName: string,
  accounts: PlaidAccountForPortfolio[],
): Promise<string[]> => {
  const credentials = await getPlaidCredentials();
  const currentPortfolios = await getPortfolios();
  const existingPlaidIds = new Set(credentials.portfolioIds || []);

  // Migration: delete the old single merged portfolio if it exists
  if (credentials.portfolioId && !existingPlaidIds.has(credentials.portfolioId)) {
    await deletePortfolio(credentials.portfolioId);
    await setRaw(PLAID_PORTFOLIO_ID_KEY, '');
  }

  const newPortfolioIds: string[] = [];
  // Track which existing IDs have already been claimed to handle same-named accounts
  const claimedIds = new Set<string>();

  for (const account of accounts) {
    const portfolioTitle =
      accounts.length === 1 ? institutionName : `${institutionName} - ${account.name || account.subtype || 'Account'}`;

    // Try to find an existing Plaid portfolio for this account
    // Match by saved ID with matching title; skip IDs already claimed by a previous account
    let existingPortfolio: Portfolio | undefined;

    for (const existingId of existingPlaidIds) {
      if (claimedIds.has(existingId)) continue;
      const found = currentPortfolios.find((p) => p.id === existingId && p.title === portfolioTitle);
      if (found) {
        existingPortfolio = found;
        claimedIds.add(existingId);
        break;
      }
    }

    if (existingPortfolio) {
      // Update existing portfolio
      await updatePortfolio({
        ...existingPortfolio,
        title: portfolioTitle,
        symbols: account.symbols,
      });
      newPortfolioIds.push(existingPortfolio.id);
    } else {
      // Create new portfolio for this account
      const id = await savePortfolio({
        title: portfolioTitle,
        strategy: '',
        symbols: account.symbols,
        allocations: [],
      });
      newPortfolioIds.push(id);
    }
  }

  // Delete any Plaid portfolios that are no longer in the account list
  // (e.g., user closed an account at their brokerage)
  for (const oldId of existingPlaidIds) {
    if (!newPortfolioIds.includes(oldId)) {
      await deletePortfolio(oldId);
    }
  }

  // Save the new portfolio IDs and account mapping
  await setRaw(PLAID_PORTFOLIO_IDS_KEY, JSON.stringify(newPortfolioIds));

  // Build portfolioId -> accountId map for holdings filtering
  const accountMap: Record<string, string> = {};
  for (let i = 0; i < accounts.length; i++) {
    if (newPortfolioIds[i]) {
      accountMap[newPortfolioIds[i]] = accounts[i].accountId;
    }
  }
  await setRaw(PLAID_PORTFOLIO_ACCOUNT_MAP_KEY, JSON.stringify(accountMap));

  // Auto-select the first Plaid portfolio only when:
  //   1. Nothing is currently selected, OR
  //   2. The selected portfolio was a Plaid portfolio that no longer exists
  //      (e.g. the account was closed/unlinked during this sync)
  // Do NOT override when the user has a manual portfolio selected — its ID
  // won't appear in newPortfolioIds but that doesn't mean it's gone.
  const selectedId = localStorage.getItem('selectedPortfolioId');
  // Also treat the legacy single portfolio ID as a "known Plaid ID" so migrating
  // users get auto-selected to their new per-account portfolios if the old merged
  // portfolio was their active selection when this function first runs.
  const selectedWasPlaidAndRemoved =
    selectedId !== null &&
    (existingPlaidIds.has(selectedId) || selectedId === credentials.portfolioId) &&
    !newPortfolioIds.includes(selectedId);
  if (!selectedId || selectedWasPlaidAndRemoved) {
    if (newPortfolioIds.length > 0) {
      localStorage.setItem('selectedPortfolioId', newPortfolioIds[0]);
    }
  }

  return newPortfolioIds;
};

// ============================================================================
// Plaid Holdings Cache Storage
// ============================================================================

const PLAID_HOLDINGS_CACHE_KEY = 'plaid_holdings_cache';

export interface PlaidHoldingCached {
  accountId: string;
  symbol: string;
  name: string;
  quantity: number;
  costBasis: number | null;
  currentValue: number;
  type: string;
  gainLoss: number | null;
  gainLossPercent: number | null;
}

export interface PlaidAccountCached {
  accountId: string;
  name: string;
  type: string;
  subtype: string;
  mask: string | null;
}

export interface PlaidHoldingsCache {
  holdings: PlaidHoldingCached[];
  accounts: PlaidAccountCached[];
  totalValue: number;
  lastFetched: number; // timestamp in ms
  refreshCountToday: number;
  refreshDate: string; // YYYY-MM-DD for daily reset
  unsupportedSymbols?: string[];
}

/**
 * Get cached Plaid holdings data.
 * Returns null if no cache exists.
 */
export const getPlaidHoldingsCache = async (): Promise<PlaidHoldingsCache | null> => {
  const cache = await get<PlaidHoldingsCache>(PLAID_HOLDINGS_CACHE_KEY);
  return cache || null;
};

/**
 * Save Plaid holdings data to cache.
 */
export const savePlaidHoldingsCache = async (data: PlaidHoldingsCache): Promise<void> => {
  await set(PLAID_HOLDINGS_CACHE_KEY, data);
};

/**
 * Clear the Plaid holdings cache.
 */
export const clearPlaidHoldingsCache = async (): Promise<void> => {
  await remove(PLAID_HOLDINGS_CACHE_KEY);
};

// ============================================================================
// Plaid Snapshots - Historical portfolio value tracking for charts
// ============================================================================

const PLAID_SNAPSHOTS_KEY = 'plaid_snapshots';
const MAX_PLAID_SNAPSHOTS = 365;

export interface PlaidSnapshot {
  date: string; // ISO date: "2024-12-16T10:30:00Z"
  totalValue: number; // Total portfolio value at this point
}

/**
 * Get all Plaid snapshots for chart history.
 */
export const getPlaidSnapshots = async (): Promise<PlaidSnapshot[]> => {
  const data = await get<PlaidSnapshot[]>(PLAID_SNAPSHOTS_KEY);
  return data || [];
};

/**
 * Add a new snapshot to the history.
 * Deduplicates by date (YYYY-MM-DD), keeping the latest value for each day.
 */
export const addPlaidSnapshot = async (snapshot: PlaidSnapshot): Promise<void> => {
  const existing = await getPlaidSnapshots();
  const snapshotDay = snapshot.date.slice(0, 10); // Extract YYYY-MM-DD

  // Filter out any existing snapshot from the same day
  const filtered = existing.filter((s) => s.date.slice(0, 10) !== snapshotDay);

  const next = [...filtered, snapshot].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  await set(PLAID_SNAPSHOTS_KEY, next.slice(-MAX_PLAID_SNAPSHOTS));
};

/**
 * Clear all Plaid snapshots (when disconnecting Plaid).
 */
export const clearPlaidSnapshots = async (): Promise<void> => {
  await Preferences.remove({ key: PLAID_SNAPSHOTS_KEY });
};
